import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper
} from '@mui/material';
import MainCard from '../../components/MainCard';
import Sidebar from "../../menu-items/Sidebar";
import axios from 'axios';
import Cookies from "js-cookie";

const FEEDBACK_ENDPOINT = 'admin/reports/user_feedback';

function normalizeResults(payload) {
  // 1) If it’s already an array
  if (Array.isArray(payload)) return payload;

  // 2) Common shapes
  const candidates = ['results', 'data', 'items', 'rows', 'list', 'content', 'records'];
  for (const key of candidates) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }

  // 3) Nested common shape: { results: { data: [...] } }
  if (payload?.results?.data && Array.isArray(payload.results.data)) {
    return payload.results.data;
  }

  // 4) If it’s a single object (one record), wrap in array
  if (payload && typeof payload === 'object') return [payload];

  // 5) Fallback to empty array
  return [];
}

// Build headers as union of keys, escape values for CSV
function toCSV(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return 'No data';

  const headerSet = new Set();
  rows.forEach((r) => Object.keys(r || {}).forEach((k) => headerSet.add(k)));
  const headers = Array.from(headerSet);

  const escapeCell = (val) => {
    const s = val === null || val === undefined ? '' : String(val);
    // Escape quotes by doubling them, wrap in quotes to guard commas/newlines
    return `"${s.replace(/"/g, '""')}"`;
  };

  const lines = [];
  lines.push(headers.join(','));
  for (const row of rows) {
    const line = headers.map((h) => escapeCell(row?.[h]));
    lines.push(line.join(','));
  }
  return lines.join('\n');
}

const Userfeedback = () => {
  const [rows, setRows] = useState([]);          // always an array
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setErrorMsg('');

      const base = process.env.REACT_APP_BASE_URL || '';
      const url = `${base}${FEEDBACK_ENDPOINT}`;

      // Use axios default header if set, else try cookie
      const bearer =
        axios.defaults.headers.common.Authorization ||
        Cookies.get('Authorization') ||
        Cookies.get('token') ||
        '';

      const config = bearer
        ? { headers: { Authorization: bearer } }
        : undefined;

      try {
        const response = await axios.get(url, config);
        const normalized = normalizeResults(response?.data);
        setRows(normalized);
      } catch (err) {
        console.error('Error fetching data:', err);
        setErrorMsg('Failed to load user feedback.');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleChangePage = (_evt, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const downloadAsCSV = () => {
    const csv = toCSV(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', 'user_feedback.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const displayedData = useMemo(() => {
    if (!Array.isArray(rows)) return [];
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  return (
    <Box sx={{ border: '1px solid #ccc', padding: 5, height: '100vh' }}>
      <Grid container rowSpacing={2.5} columnSpacing={2.75}>
        <Grid item xs={12} md={3} lg={2}>
          <Sidebar />
        </Grid>

        <Grid item xs={12} md={9} lg={10}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 'bold', fontSize: '2rem', mb: '20px' }}
            >
              User Feedback
            </Typography>

            <Button
              variant="contained"
              onClick={downloadAsCSV}
              sx={{ borderRadius: '20px', textTransform: 'none', px: 2, mb: '20px' }}
              disabled={!rows?.length}
            >
              Download as CSV
            </Button>
          </Box>

          <MainCard title="">
            <TableContainer
              component={Paper}
              sx={{ borderRadius: '15px', boxShadow: 3, overflow: 'hidden', mt: 2 }}
            >
              <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1976d2', color: '#fff' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    
                    <TableCell sx={{ fontWeight: 'bold' }}>Created At</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Treatment Plan ID</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Practice Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Question</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Answer</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">Loading…</TableCell>
                    </TableRow>
                  ) : errorMsg ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">{errorMsg}</TableCell>
                    </TableRow>
                  ) : displayedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayedData.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          '&:hover': { backgroundColor: '#f5f5f5', cursor: 'pointer' },
                          borderBottom: '1px solid #ddd',
                        }}
                      >
                        <TableCell sx={{ borderTopLeftRadius: index === 0 ? '15px' : 0 }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        
                        <TableCell>{row?.created_at ?? '-'}</TableCell>
                        <TableCell>{row?.treatment_plan_id ?? '-'}</TableCell>
                        <TableCell>{row?.practice_name ?? '-'}</TableCell>
                        <TableCell>{row?.question ?? '-'}</TableCell>
                        <TableCell>{row?.answer ?? '-'}</TableCell>
                        <TableCell sx={{ borderTopRightRadius: index === displayedData.length - 1 ? '15px' : 0 }}>
                          {row?.message ?? '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[20, 50, 100, 200]}
              component="div"
              count={Array.isArray(rows) ? rows.length : 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </MainCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Userfeedback;
